import FuseUtils from '@fuse/utils';
import moment from 'moment';
import NP from 'number-precision';

export const useCodigoInitialValue = (compraId) => {
  return compraId && `${compraId}`.padStart(3, '0');
};

export const useFornecedorInitialValue = (compra) => {
  return {
    id: compra?.fornecedor?.id || '',
    pessoa: {
      id: compra?.fornecedor?.pessoa?.id || '',
      nome: compra?.fornecedor?.pessoa?.nome || '',
    },
  };
};

export const useCategoriaInitialValue = (compra) => {
  return compra?.categoria?.id
    ? {
        id: compra?.categoria?.id || '',
        indice: compra?.categoria?.indice || '',
        nome: compra?.categoria?.nome || '',
      }
    : '';
};

export const useCategoriasInitialValue = (compra) => {
  return compra?.categorias ? compra.categorias : [{ id: undefined, classe: {}, valorBruto: undefined }];
};

const UUID = FuseUtils.generateGUID();

export const useProdutosInitialValue = (compra) => {
  return compra?.comprasProdutos?.length
    ? compra.comprasProdutos.map((produto) => ({
        ...produto,
        key: produto.id,
      }))
    : [{ key: UUID }];
};

const mapPagamentoParcelado = ({
  dataVencimento,
  valorParcela,
  contaPagarId,
  dataPagamento,
  dataLiquidacao,
  contaBaixada,
}) => ({
  dataVencimento: dataVencimento ? moment(dataVencimento) : null,
  valorParcela,
  contaPagarId,
  dataPagamento,
  dataLiquidacao,
  contaBaixada,
});

export const usePagamentoInitialValue = (compra) => {
  const HOJE = moment();

  return {
    condicaoPagamento: compra?.pagamento?.condicaoPagamento || 'A_VISTA',
    avista:
      compra?.pagamento?.condicaoPagamento !== 'PARCELADO'
        ? {
            condicaoPagamento: compra?.pagamento?.condicaoPagamento || 'A_VISTA',
            dataVencimento: compra?.pagamento?.avista?.dataVencimento
              ? moment(compra?.pagamento?.avista?.dataVencimento)
              : HOJE,
            compraPaga: !!compra?.pagamento?.avista?.dataPagamento,
            dataPagamento: compra?.pagamento?.avista?.dataPagamento
              ? moment(compra?.pagamento?.avista?.dataPagamento)
              : null,
            dataLiquidacao: compra?.pagamento?.avista?.dataLiquidacao
              ? moment(compra?.pagamento?.avista?.dataLiquidacao)
              : null,
            formaPagamento: compra?.pagamento?.avista?.formaPagamento || '',
            numeroCheque: compra?.pagamento?.avista?.numeroCheque || '',
            conta: compra?.pagamento?.avista?.conta?.id ? compra?.pagamento?.avista?.conta : '',
            contaPagarId: compra?.pagamento?.avista?.contaPagarId || null,
            contaBaixada: compra?.pagamento?.avista?.contaBaixada || null,
            valorMulta: compra?.pagamento?.avista?.valorMulta || '',
            valorJuros: compra?.pagamento?.avista?.valorJuros || '',
            valorDesconto: compra?.pagamento?.avista?.valorDesconto || '',
            valorTotalAcrescimos:
              compra?.pagamento?.avista?.valorMulta +
                compra?.pagamento?.avista?.valorJuros -
                compra?.pagamento?.avista?.valorDesconto || '',
            acrescimosDescontos: compra?.pagamento?.avista?.acrescimosDescontos,
          }
        : null,
    parcelado:
      compra?.pagamento?.condicaoPagamento === 'PARCELADO'
        ? {
            condicaoPagamento: compra?.pagamento?.condicaoPagamento || 'PARCELADO',
            parcelas: compra?.pagamento?.parcelado?.length ? compra.pagamento.parcelado.map(mapPagamentoParcelado) : [],
            quantidade: compra?.pagamento?.parcelado?.length,
          }
        : null,
  };
};

export const useAproveitamentoCreditoInitialValue = (compra) => {
  return {
    valorCreditoIbs: compra?.aproveitamentoCredito?.valorCreditoIbs || null,
    valorCreditoCbs: compra?.aproveitamentoCredito?.valorCreditoCbs || null,
    tipoAproveitamentoCredito: compra?.aproveitamentoCredito?.tipoAproveitamentoCredito || 'NAO_APROVEITA_CREDITO',
    aliquotaIbs: compra?.aproveitamentoCredito?.aliquotaIbs || null,
    aliquotaCbs: compra?.aproveitamentoCredito?.aliquotaCbs || null,
    percentualCredito: compra?.aproveitamentoCredito?.percentualCredito || null,
    observacaoJustificativa: compra?.aproveitamentoCredito?.observacaoJustificativa || '',
  };
};

export const useCompraServicoInitialValue = (compraServico) => {
  return {
    descricao: compraServico?.descricao || '',
    anexos: compraServico?.anexos || [],
    notaFiscal: {
      valorBruto: compraServico?.notaFiscal?.valorBruto || '',
      municipio: compraServico?.notaFiscal?.municipio || '',
      codigoServicoMunicipal: compraServico?.notaFiscal?.codigoServicoMunicipal || '',
      dataEmissao: compraServico?.notaFiscal?.dataEmissao ? moment(compraServico?.notaFiscal?.dataEmissao) : null,
      serie: compraServico?.notaFiscal?.serie || '',
      numero: compraServico?.notaFiscal?.numero || '',
      temRetencao: !!compraServico?.retencoes?.length,
      retencoes:
        compraServico?.retencoes?.map((retencao) => ({
          ...retencao,
          dataVencimento: retencao?.dataVencimento ? moment(retencao.dataVencimento) : null,
          dataPagamento: retencao?.dataPagamento,
          dataLiquidacao: retencao?.dataLiquidacao,
          contaPagarId: retencao?.contaPagarId,
          key: retencao?.id || FuseUtils.generateGUID(),
          contaBaixada: retencao?.contaBaixada,
        })) || [],
    },
  };
};

export const useValorLiquidoInitialValue = (compra) => {
  const valorImpostos =
    compra?.compraServico?.retencoes
      ?.map((retencao) => retencao.valor)
      ?.reduce((acc, valor) => NP.plus(acc, valor || 0), 0) || 0;

  const valorBruto = compra?.categorias?.reduce((soma, categoria) => soma + categoria.valorBruto, 0);

  return NP.minus(valorBruto || 0, valorImpostos);
};
